import React, { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useCompanyContext } from '@/hooks/use-company-context';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  ArrowLeft, 
  Truck, 
  Route, 
  Wrench, 
  DollarSign, 
  Bell,
  MapPin,
  Calendar,
  Gauge,
  Fuel
} from 'lucide-react';
import { useVehicleTrips } from '@/hooks/use-vehicle-trips';
import { TripsList } from '@/components/gps/TripsList';
import { GpsTrip } from '@/hooks/use-gps-trips';
import { useFleetAlerts } from '@/hooks/use-fleet-alerts';
import { formatWorkOrderNumber } from '@/hooks/use-work-orders';

interface Vehicle {
  id: string;
  plate: string;
  renavam?: string;
  vin?: string;
  make?: string;
  model?: string;
  year?: number;
  category: string;
  fuel_type: string;
  odometer: number;
  status: string;
  created_at: string;
}

export default function VehicleDetails() {
  const { vehicleId } = useParams<{ vehicleId: string }>();
  const { companyId } = useCompanyContext();
  const navigate = useNavigate();
  const [selectedTab, setSelectedTab] = useState('info');

  // Fetch vehicle data
  const { data: vehicle, isLoading: vehicleLoading } = useQuery({
    queryKey: ['vehicle-detail', vehicleId],
    queryFn: async () => {
      if (!vehicleId) throw new Error('Vehicle ID required');
      
      const { data, error } = await supabase
        .from('fleet_vehicles')
        .select('*')
        .eq('id', vehicleId)
        .single();

      if (error) throw error;
      return data as Vehicle;
    },
    enabled: !!vehicleId
  });

  // Fetch trips
  const { 
    data: trips = [], 
    isLoading: tripsLoading 
  } = useVehicleTrips(vehicleId, companyId);

  // Fetch work orders for this vehicle
  const { data: workOrders = [], isLoading: workOrdersLoading } = useQuery({
    queryKey: ['vehicle-work-orders', vehicleId, companyId],
    queryFn: async () => {
      if (!vehicleId || !companyId) return [];
      
      const { data, error } = await supabase
        .from('fleet_work_orders')
        .select(`
          *,
          fleet_vehicles!inner(id, plate, make, model)
        `)
        .eq('vehicle_id', vehicleId)
        .eq('company_id', companyId)
        .order('opened_at', { ascending: false });
      
      if (error) throw error;
      return data || [];
    },
    enabled: !!vehicleId && !!companyId
  });

  // Fetch fuel logs for this vehicle
  const { data: fuelLogs = [], isLoading: fuelLogsLoading } = useQuery({
    queryKey: ['vehicle-fuel-logs', vehicleId, companyId],
    queryFn: async () => {
      if (!vehicleId || !companyId) return [];
      
      const { data, error } = await supabase
        .from('fleet_fuel_logs')
        .select('*')
        .eq('vehicle_id', vehicleId)
        .eq('company_id', companyId)
        .order('occurred_at', { ascending: false });
      
      if (error) throw error;
      return data || [];
    },
    enabled: !!vehicleId && !!companyId
  });

  // Calculate cost statistics
  const costStats = {
    totalFuelCost: fuelLogs.reduce((sum, log) => sum + ((log.liters || 0) * (log.price_unit || 0)), 0),
    totalFuelLiters: fuelLogs.reduce((sum, log) => sum + (log.liters || 0), 0),
    totalMaintenanceCost: workOrders.reduce((sum, order) => sum + (order.total_cost || 0), 0),
    totalCost: 0 // Será calculado abaixo
  };
  costStats.totalCost = costStats.totalFuelCost + costStats.totalMaintenanceCost;

  // Fetch alerts for this vehicle
  const { data: allAlerts = [], isLoading: alertsLoading } = useFleetAlerts(companyId);
  const vehicleAlerts = allAlerts.filter(alert => alert.vehicle_id === vehicleId);

  const handleTripSelect = (trip: GpsTrip) => {
    // Navigate to trips page with selected trip (could be enhanced)
    console.log('Selected trip:', trip);
  };

  const handleTripReplay = (trip: GpsTrip) => {
    // Navigate to trips page with replay mode (could be enhanced)
    console.log('Replay trip:', trip);
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <Badge className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">Ativo</Badge>;
      case 'maintenance':
        return <Badge className="bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200">Manutenção</Badge>;
      case 'inactive':
        return <Badge className="bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200">Inativo</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const getCategoryLabel = (category: string) => {
    const categories: Record<string, string> = {
      car: 'Carro',
      truck: 'Caminhão',
      van: 'Van',
      pickup: 'Pickup',
      motorcycle: 'Moto'
    };
    return categories[category] || category || 'N/A';
  };

  const getFuelTypeLabel = (fuelType: string) => {
    const types: Record<string, string> = {
      gasoline: 'Gasolina',
      diesel: 'Diesel',
      ethanol: 'Etanol',
      flex: 'Flex',
      electric: 'Elétrico',
      hybrid: 'Híbrido'
    };
    return types[fuelType] || fuelType || 'N/A';
  };

  // Calculate trip statistics
  const tripStats = {
    totalTrips: trips.length,
    totalDistance: trips.reduce((sum, trip) => sum + trip.distance_km, 0),
    totalDuration: trips.reduce((sum, trip) => sum + trip.duration_minutes, 0),
    totalIdleTime: trips.reduce((sum, trip) => sum + trip.idle_time_minutes, 0)
  };

  if (vehicleLoading) {
    return (
      <div className="container mx-auto p-6">
        <div className="flex items-center justify-center h-64">
          <p>Carregando detalhes do veículo...</p>
        </div>
      </div>
    );
  }

  if (!vehicle) {
    return (
      <div className="container mx-auto p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <h2 className="text-lg font-semibold mb-2">Veículo não encontrado</h2>
            <Button variant="outline" asChild>
              <Link to="..">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Voltar
              </Link>
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
        <div>
          <Button variant="ghost" size="sm" asChild className="mb-2">
            <Link to="..">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Voltar
            </Link>
          </Button>
          <div className="flex items-center gap-3">
            <Truck className="h-8 w-8 text-primary" />
            <div>
              <h1 className="text-3xl font-bold">{vehicle.plate}</h1>
              <p className="text-muted-foreground">
                {vehicle.make} {vehicle.model} {vehicle.year ? `(${vehicle.year})` : ''}
              </p>
            </div>
            {getStatusBadge(vehicle.status)}
          </div>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
                <Route className="h-5 w-5 text-blue-600 dark:text-blue-200" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Viagens</p>
                <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">{tripStats.totalTrips}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 dark:bg-green-900 rounded-lg">
                <MapPin className="h-5 w-5 text-green-600 dark:text-green-200" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Distância Total</p>
                <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                  {tripStats.totalDistance.toFixed(0)} km
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 dark:bg-purple-900 rounded-lg">
                <Gauge className="h-5 w-5 text-purple-600 dark:text-purple-200" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Odômetro</p>
                <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                  {vehicle.odometer.toLocaleString()} km
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 dark:bg-orange-900 rounded-lg">
                <Calendar className="h-5 w-5 text-orange-600 dark:text-orange-200" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Tempo Total</p>
                <p className="text-2xl font-bold text-orange-600 dark:text-orange-400">
                  {Math.floor(tripStats.totalDuration / 60)}h
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={selectedTab} onValueChange={setSelectedTab} className="w-full">
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="info">
            <Truck className="h-4 w-4 mr-2" />
            Informações
          </TabsTrigger>
          <TabsTrigger value="trips">
            <Route className="h-4 w-4 mr-2" />
            Viagens ({tripStats.totalTrips})
          </TabsTrigger>
          <TabsTrigger value="maintenance">
            <Wrench className="h-4 w-4 mr-2" />
            Manutenção
          </TabsTrigger>
          <TabsTrigger value="costs">
            <DollarSign className="h-4 w-4 mr-2" />
            Custos
          </TabsTrigger>
          <TabsTrigger value="alerts">
            <Bell className="h-4 w-4 mr-2" />
            Alertas
          </TabsTrigger>
        </TabsList>

        {/* Tab: Informações */}
        <TabsContent value="info">
          <Card>
            <CardHeader>
              <CardTitle>Informações do Veículo</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Placa</p>
                    <p className="text-lg font-semibold">{vehicle.plate}</p>
                  </div>
                  {vehicle.renavam && (
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">RENAVAM</p>
                      <p className="text-lg">{vehicle.renavam}</p>
                    </div>
                  )}
                  {vehicle.vin && (
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Chassi (VIN)</p>
                      <p className="text-lg font-mono">{vehicle.vin}</p>
                    </div>
                  )}
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Categoria</p>
                    <p className="text-lg">{getCategoryLabel(vehicle.category)}</p>
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Marca/Modelo</p>
                    <p className="text-lg font-semibold">{vehicle.make} {vehicle.model}</p>
                  </div>
                  {vehicle.year && (
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Ano</p>
                      <p className="text-lg">{vehicle.year}</p>
                    </div>
                  )}
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Combustível</p>
                    <p className="text-lg">{getFuelTypeLabel(vehicle.fuel_type)}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Quilometragem</p>
                    <p className="text-lg font-semibold">{vehicle.odometer.toLocaleString()} km</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab: Viagens */}
        <TabsContent value="trips">
          <TripsList
            trips={trips}
            loading={tripsLoading}
            onTripSelect={handleTripSelect}
            onTripReplay={handleTripReplay}
            onFiltersChange={() => {}}
          />
        </TabsContent>

        {/* Tab: Manutenção */}
        <TabsContent value="maintenance">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>Histórico de Manutenções</span>
                <Badge variant="secondary">{workOrders.length} ordens</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {workOrdersLoading ? (
                <div className="text-center py-8">
                  <p className="text-muted-foreground">Carregando...</p>
                </div>
              ) : workOrders.length === 0 ? (
                <div className="text-center py-8">
                  <Wrench className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">Nenhuma manutenção registrada para este veículo</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {workOrders.map((order: any) => (
                    <div 
                      key={order.id} 
                      className="border rounded-lg p-4 hover:bg-muted/50 transition-colors"
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-lg">
                              {formatWorkOrderNumber(order.order_number)}
                            </span>
                            <Badge variant={order.status === 'open' ? 'default' : order.status === 'in_progress' ? 'secondary' : 'outline'}>
                              {order.status === 'open' ? 'Aberta' : order.status === 'in_progress' ? 'Em Andamento' : 'Fechada'}
                            </Badge>
                          </div>
                          <p className="text-sm text-muted-foreground mt-1">
                            {order.description || 'Sem descrição'}
                          </p>
                        </div>
                        {order.total_cost && (
                          <div className="text-right">
                            <p className="text-lg font-bold text-green-600">
                              {new Intl.NumberFormat('pt-BR', { 
                                style: 'currency', 
                                currency: 'BRL' 
                              }).format(order.total_cost)}
                            </p>
                          </div>
                        )}
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4 text-sm mt-3 pt-3 border-t">
                        <div>
                          <p className="text-muted-foreground">Aberta em:</p>
                          <p className="font-medium">
                            {new Date(order.opened_at).toLocaleDateString('pt-BR')}
                          </p>
                        </div>
                        {order.closed_at && (
                          <div>
                            <p className="text-muted-foreground">Fechada em:</p>
                            <p className="font-medium">
                              {new Date(order.closed_at).toLocaleDateString('pt-BR')}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab: Custos */}
        <TabsContent value="costs">
          <div className="space-y-4">
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium text-muted-foreground">
                    Custo Total
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-2xl font-bold">
                    {new Intl.NumberFormat('pt-BR', { 
                      style: 'currency', 
                      currency: 'BRL' 
                    }).format(costStats.totalCost)}
                  </p>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                    <Fuel className="h-4 w-4" />
                    Combustível
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-2xl font-bold text-blue-600">
                    {new Intl.NumberFormat('pt-BR', { 
                      style: 'currency', 
                      currency: 'BRL' 
                    }).format(costStats.totalFuelCost)}
                  </p>
                  <p className="text-sm text-muted-foreground mt-1">
                    {costStats.totalFuelLiters.toFixed(1)} litros
                  </p>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                    <Wrench className="h-4 w-4" />
                    Manutenção
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-2xl font-bold text-orange-600">
                    {new Intl.NumberFormat('pt-BR', { 
                      style: 'currency', 
                      currency: 'BRL' 
                    }).format(costStats.totalMaintenanceCost)}
                  </p>
                  <p className="text-sm text-muted-foreground mt-1">
                    {workOrders.length} ordens de serviço
                  </p>
                </CardContent>
              </Card>
            </div>
            
            {/* Recent Fuel Logs */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span>Últimos Abastecimentos</span>
                  <Badge variant="secondary">{fuelLogs.length} registros</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {fuelLogsLoading ? (
                  <div className="text-center py-8">
                    <p className="text-muted-foreground">Carregando...</p>
                  </div>
                ) : fuelLogs.length === 0 ? (
                  <div className="text-center py-8">
                    <Fuel className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground">Nenhum abastecimento registrado</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {fuelLogs.slice(0, 5).map((log: any) => (
                      <div 
                        key={log.id}
                        className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50 transition-colors"
                      >
                        <div>
                          <div className="flex items-center gap-2">
                            <Fuel className="h-4 w-4 text-blue-600" />
                            <span className="font-medium">{log.liters.toFixed(1)}L</span>
                            <span className="text-sm text-muted-foreground">
                              • {new Date(log.occurred_at).toLocaleDateString('pt-BR')}
                            </span>
                          </div>
                          <p className="text-sm text-muted-foreground mt-1">
                            R$ {log.price_unit?.toFixed(2)}/litro • {log.odometer?.toLocaleString()} km
                          </p>
                        </div>
                        <p className="text-lg font-bold text-blue-600">
                          {new Intl.NumberFormat('pt-BR', { 
                            style: 'currency', 
                            currency: 'BRL' 
                          }).format((log.liters || 0) * (log.price_unit || 0))}
                        </p>
                      </div>
                    ))}
                    {fuelLogs.length > 5 && (
                      <p className="text-sm text-center text-muted-foreground pt-2">
                        + {fuelLogs.length - 5} abastecimentos anteriores
                      </p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Tab: Alertas */}
        <TabsContent value="alerts">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>Alertas do Veículo</span>
                <Badge variant="secondary">{vehicleAlerts.length} alertas</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {alertsLoading ? (
                <div className="text-center py-8">
                  <p className="text-muted-foreground">Carregando...</p>
                </div>
              ) : vehicleAlerts.length === 0 ? (
                <div className="text-center py-8">
                  <Bell className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">Nenhum alerta registrado para este veículo</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {vehicleAlerts.map((alert: any) => {
                    const priorityColor = alert.priority === 'high' ? 'text-red-600' : 
                                         alert.priority === 'medium' ? 'text-orange-600' : 
                                         'text-blue-600';
                    const statusColor = alert.status === 'pending' ? 'destructive' : 
                                       alert.status === 'acknowledged' ? 'secondary' : 
                                       'outline';
                    
                    return (
                      <div 
                        key={alert.id}
                        className="border rounded-lg p-4 hover:bg-muted/50 transition-colors"
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <Bell className={`h-4 w-4 ${priorityColor}`} />
                            <span className="font-semibold">{alert.title}</span>
                          </div>
                          <Badge variant={statusColor}>
                            {alert.status === 'pending' ? 'Pendente' : 
                             alert.status === 'acknowledged' ? 'Reconhecido' : 
                             'Resolvido'}
                          </Badge>
                        </div>
                        
                        {alert.description && (
                          <p className="text-sm text-muted-foreground mb-3">
                            {alert.description}
                          </p>
                        )}
                        
                        <div className="flex items-center justify-between text-sm pt-2 border-t">
                          <div className="flex items-center gap-4">
                            <div>
                              <p className="text-muted-foreground">Tipo:</p>
                              <p className="font-medium capitalize">{alert.alert_type.replace('_', ' ')}</p>
                            </div>
                            <div>
                              <p className="text-muted-foreground">Prioridade:</p>
                              <p className={`font-medium capitalize ${priorityColor}`}>
                                {alert.priority === 'high' ? 'Alta' : 
                                 alert.priority === 'medium' ? 'Média' : 
                                 'Baixa'}
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-muted-foreground">Disparado em:</p>
                            <p className="font-medium">
                              {new Date(alert.triggered_at).toLocaleDateString('pt-BR')}
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
